// @ts-strict-ignore
import Form from 'antd/lib/form';
import Modal from 'antd/lib/modal';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Input from 'antd/lib/input';
import { AppComponentProps, Nullable } from '@/typescriptHelpers';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectVCSIntegration,
    selectFeatureManagementIntegration,
    selectRequiresFixRequestsApprovals,
    selectFeatureWorkItem,
    selectAllTicketOnlyItems,
    selectIntegrationsWithCapabilityFromInstallations,
    selectAppIntegrations,
    selectIsAutomationEnabledForReleaseType,
} from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { SearchOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import {
    FeatureItem,
    FixRequestFormFieldSettings,
    FixRequestFormFieldValue,
    IntegrationCapability,
    PRItem,
    ReleaseType,
} from '@/types';
import TicketStatus from './TicketStatus';
import CodeStatus from './CodeStatus';
import { codeIsCommit, runeCount, getFixRequestFormFieldId } from '@/helpers';
import MarkdownEditor from '../HOCs/MarkdownEditor';
import MarkdownComponent from '../HOCs/Markdown';
import { renderIcons, renderItemDesc, renderTicketOwner, renderCodeOwner } from './FeatureReadinessItem';
import { selectAllOpenPRs, selectCommitsOnlyOnWorkingBranch } from '@/selectors/memoizedSelectors';
import WorkItemSelect from './WorkItemSelect';
import { globalMessage } from '@/utils/globalMessage';
import AutomationSettingsBanner from '../sharedComponents/AutomationSettingsBanner';
import { ReleaseStepUpdatable } from '@/actions/updatable';
import { AddFixRequest, tryUpdateReleaseStep } from '@/actions/releaseStep';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { useGetPendingAssociationTicketsQuery } from '@/middlewares/defaultApi';
import { AutoComplete, Radio } from 'antd/lib';
import { useWatch } from 'antd/es/form/Form';
import Button from 'antd/lib/button';
import Icon from '@ant-design/icons/lib/components/Icon';
import Alert from 'antd/lib/alert/Alert';

interface AddAFixModalProps extends AppComponentProps {
    releaseId: string;
    releaseType: ReleaseType;
    isModalOpen: boolean;
    onCancel: () => void;
    workingBranchEnabled: boolean;
    isAutoMergeRunwayPRsEnabled: boolean;
    requiresFixRequestsApprovals: boolean;
    fixRequestFormFieldsSettings: FixRequestFormFieldSettings[];
    featureItemId?: string;
    stepRunId: string;
    autoCherryPickAutomationId: Nullable<string>;
    autoMergeRunwayPRsAutomationId: Nullable<string>;
}

const AddAFixModal: React.FC<AddAFixModalProps> = (props: AddAFixModalProps) => {
    const dispatch = useDispatch();

    const openPRs = useSelector((state: RootState) => selectAllOpenPRs(state, props.stepRunId));

    const commitsOnlyOnWorkingBranch = useSelector((state: RootState) =>
        selectCommitsOnlyOnWorkingBranch(state, props.stepRunId),
    );

    const ticketOnlyItems = useSelector((state: RootState) => selectAllTicketOnlyItems(state, props.stepRunId));

    const featureItem = useSelector((state: RootState) => {
        if (props.featureItemId) {
            return selectFeatureWorkItem(state, props.stepRunId, props.featureItemId);
        } else {
            return null;
        }
    });

    const requireFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const vcsIntegration = useSelector((state: RootState) => selectVCSIntegration(state, props.appId));
    const featureManagementIntegration = useSelector((state: RootState) =>
        selectFeatureManagementIntegration(state, props.appId),
    );

    const isAutoCherryPickAutomationEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabledForReleaseType(
            state,
            props.appId,
            props.autoCherryPickAutomationId,
            props.releaseType,
        ),
    );

    const [selectedOption, setSelectedOption] = React.useState(featureItem);

    const [form] = Form.useForm();

    const customFieldInitialValues = props.fixRequestFormFieldsSettings.reduce(
        (acc, f) => {
            acc[`customField_${getFixRequestFormFieldId(f)}`] = f.prePopulatedText || null;
            return acc;
        },
        {} as Record<string, string | null>,
    );

    const addFixRequestIsLoading = useIsLoading(ReleaseStepUpdatable.AddFixRequest, selectedOption?.id);
    const addFixRequestSuccess = useUpdated(ReleaseStepUpdatable.AddFixRequest, selectedOption?.id);
    const addFixRequestError = useError(ReleaseStepUpdatable.AddFixRequest, selectedOption?.id);

    // The OK button only shows loading once the request is dispatched, so a second click or Enter
    // during validateFields would otherwise send a duplicate add.
    const isSubmittingRef = React.useRef(false);
    React.useEffect(() => {
        if (!addFixRequestIsLoading) {
            isSubmittingRef.current = false;
        }
    }, [addFixRequestIsLoading]);

    const issueTrackingIntegrationsFromAutomation = useSelector((state: RootState) => {
        const integrations = selectAppIntegrations(state, props.appId) || [];
        const integrationInstallationIds = integrations.map((integration) => integration.installationId);
        return selectIntegrationsWithCapabilityFromInstallations(
            state,
            props.appId,
            IntegrationCapability.FeatureManagement,
            integrationInstallationIds,
        );
    });

    const [shouldChooseFromAssociatedWorkItems, setShouldChooseFromAssociatedWorkItems] = React.useState(true);
    const [searchCriteria, setSearchCriteria] = React.useState(null);
    const [pendingAssociationTickets, setPendingAssociationTickets] = React.useState({} as Record<string, FeatureItem>);
    const pendingAssociationTicketVal: string = useWatch('pendingAssociationTicket', { form: form });
    const shouldSkipPendingAssociationTicketsSearch =
        !pendingAssociationTicketVal?.trim() ||
        !!pendingAssociationTickets?.[pendingAssociationTicketVal] ||
        !issueTrackingIntegrationsFromAutomation?.length;
    const { isFetching: getPendingAssociationTicketsIsLoading, data: pendingAssociationTicketsResponse } =
        useGetPendingAssociationTicketsQuery(
            {
                appId: props.appId,
                orgId: props.orgId,
                releaseId: props.releaseId,
                searchCriteria,
            },
            { skip: !searchCriteria?.trim() || shouldSkipPendingAssociationTicketsSearch },
        );

    // const debouncedSearch = React.useCallback(
    //     debounce((input) => {
    //         setSelectedOption(null);
    //         setPendingAssociationTickets({});
    //         setSearchCriteria(input.target.value);
    //     }, 330),
    //     [],
    // );

    React.useEffect(() => {
        if (addFixRequestSuccess) {
            if (props.requiresFixRequestsApprovals) {
                globalMessage.success('Fix Request added successfully!');
            } else {
                globalMessage.success('Fix added successfully!');
            }
            props.onCancel();
        }
    }, [addFixRequestSuccess]);

    React.useEffect(() => {
        if (addFixRequestError) {
            globalMessage.error(addFixRequestError);
        }
    }, [addFixRequestError]);

    // Surface any minCharacters errors for too-short pre-populated text once the modal has finished
    // opening (the field values themselves are seeded via each Form.Item's initialValue prop). Run
    // from the modal's afterOpenChange so the Form.Items are mounted/registered (destroyOnClose).
    const seedFormFieldsOnOpen = () => {
        const fieldErrors = props.fixRequestFormFieldsSettings
            .filter((field) => {
                if (!field.minCharacters || field.minCharacters <= 0) return false;
                const val = customFieldInitialValues[`customField_${getFixRequestFormFieldId(field)}`];
                return typeof val === 'string' && val.length > 0 && runeCount(val) < field.minCharacters;
            })
            .map((field) => {
                const val = customFieldInitialValues[`customField_${getFixRequestFormFieldId(field)}`] as string;
                return {
                    name: `customField_${getFixRequestFormFieldId(field)}`,
                    errors: [`${field.name} is too short (${runeCount(val)}/${field.minCharacters})`],
                };
            });
        if (fieldErrors.length > 0) {
            form.setFields(fieldErrors);
        }
    };

    React.useEffect(() => {
        if (pendingAssociationTicketsResponse?.data) {
            setPendingAssociationTickets(pendingAssociationTicketsResponse.data);
        }
        form.validateFields(['pendingAssociationTicket']);
    }, [pendingAssociationTicketsResponse]);

    React.useEffect(() => {
        if (featureItem && featureItem.code) {
            form.setFieldsValue({ associatedItem: featureItem.id });
            setSelectedOption(featureItem);
        } else if (featureItem && !featureItem.code && featureItem.ticket) {
            form.setFieldsValue({ associatedItem: featureItem.id });
            setSelectedOption(featureItem);
        } else {
            form.setFieldsValue({ associatedItem: null });
            setSelectedOption(null);
        }
    }, [featureItem]);

    const handleOnSubmit = () => {
        if (!selectedOption || isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;

        form.validateFields()
            .then((values) => {
                const formFieldsValues: FixRequestFormFieldValue[] = props.fixRequestFormFieldsSettings.map(
                    (field) => ({
                        fieldId: getFixRequestFormFieldId(field),
                        value: values[`customField_${getFixRequestFormFieldId(field)}`] ?? '',
                    }),
                );

                const pullRequestNumber =
                    selectedOption.code && !codeIsCommit(selectedOption.code) ?
                        parseInt(selectedOption.code.identifier)
                    :   undefined;

                const request: AddFixRequest = {
                    workItems:
                        values.pendingAssociationTicket ?
                            []
                        :   [
                                {
                                    id: selectedOption.id,
                                    pullRequestNumber,
                                    ticketKey: selectedOption.ticket?.identifier,
                                    ticketUrl: selectedOption.ticket?.url,
                                },
                            ],
                    ticketKey: values.pendingAssociationTicket ? selectedOption.ticket?.identifier : null,
                    ticketUrl: values.pendingAssociationTicket ? selectedOption.ticket?.url : null,
                    formFieldsValues,
                };

                dispatch(
                    tryUpdateReleaseStep(
                        ReleaseStepUpdatable.AddFixRequest,
                        {
                            appId: props.appId,
                            orgId: props.orgId,
                            releaseId: props.releaseId,
                            stepRunId: props.stepRunId,
                            frItemId: selectedOption.id,
                        },
                        request,
                        selectedOption.id,
                    ),
                );
            })
            .catch((_) => {
                isSubmittingRef.current = false;
            });
    };

    function handleSelectWorkItemForFix(value) {
        for (const codeItem of openPRs) {
            if (codeItem.id === value) {
                setSelectedOption(codeItem);
                return;
            }
        }
        for (const codeItem of commitsOnlyOnWorkingBranch) {
            if (codeItem.id === value) {
                setSelectedOption(codeItem);
                return;
            }
        }
        for (const codeItem of ticketOnlyItems) {
            if (codeItem.id === value) {
                setSelectedOption(codeItem);
                return;
            }
        }
    }

    // WorkItemSelect (inside the Form.Item) owns onChange; keep selectedOption in sync from the field value.
    const associatedItemId = useWatch('associatedItem', form);
    React.useEffect(() => {
        if (associatedItemId) {
            handleSelectWorkItemForFix(associatedItemId);
        }
    }, [associatedItemId]);

    const formItemLayout = {
        labelCol: { span: 24 },
        wrapperCol: { span: 24 },
    };

    const displayFeatureItemDetails = () => {
        return (
            <div
                style={{
                    flexGrow: 1,
                    display: 'flex',
                    alignItems: 'center',
                    padding: 8,
                    minWidth: 0,
                    backgroundColor: runwayColors.white,
                    borderRadius: '8px',
                    border: `1px solid ${antGrey.grey5}`,
                }}
            >
                <div style={{ marginRight: 8 }}>
                    {renderIcons(
                        featureManagementIntegration?.id,
                        vcsIntegration?.id,
                        !!selectedOption.ticket,
                        !!selectedOption.code,
                        selectedOption.ignored,
                        selectedOption.explanationText,
                        {
                            backgroundColor: runwayColors.white,
                            paddingTop: 8,
                            paddingBottom: 8,
                            paddingLeft: 5,
                            paddingRight: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                        },
                        0,
                        15,
                        3,
                    )}
                </div>
                <div
                    style={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minWidth: 0,
                        backgroundColor: runwayColors.white,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            minHeight: 60,
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ width: '35%' }}>
                            <TicketStatus ticket={selectedOption.ticket} ignored={selectedOption.ignored} />
                        </div>
                        <div style={{ flexGrow: 1, marginLeft: 15, width: '32%' }}>
                            {renderItemDesc(
                                selectedOption.ticket?.description,
                                [],
                                false,
                                selectedOption.ticket?.additionalFields,
                                {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                },
                                {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                },
                            )}
                        </div>
                        <div
                            style={{
                                marginLeft: 15,
                                marginRight: 12,
                                width: 100,
                                flexShrink: 0,
                            }}
                        >
                            {renderTicketOwner(!!selectedOption.ticket, selectedOption.ticket?.owner, true)}
                        </div>
                    </div>
                    <Divider dashed style={{ margin: '10px 0px', borderColor: antGrey.grey6 }} />
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            minHeight: 60,
                            alignItems: 'center',
                        }}
                    >
                        <div style={{ width: '35%' }}>
                            <CodeStatus
                                code={selectedOption.code}
                                ignored={selectedOption.ignored}
                                workingBranchEnabled={props.workingBranchEnabled}
                                appId={props.appId}
                                isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                            />
                        </div>
                        <div style={{ flexGrow: 1, marginLeft: 15, width: '32%' }}>
                            {renderItemDesc(
                                selectedOption.code ?
                                    codeIsCommit(selectedOption.code) ? selectedOption.code.message
                                    :   (selectedOption.code as PRItem).title
                                :   null,
                                [],
                                selectedOption.ignored,
                                null,
                                {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                },
                                {
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                },
                            )}
                        </div>
                        <div
                            style={{
                                marginLeft: 15,
                                marginRight: 12,
                                width: 100,
                                flexShrink: 0,
                            }}
                        >
                            {renderCodeOwner(selectedOption.code, true)}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={props.requiresFixRequestsApprovals ? 'Request approval for a fix' : 'Pull in work item as fix'}
            okText={props.requiresFixRequestsApprovals ? 'Submit Request' : 'Pull in as fix'}
            open={props.isModalOpen}
            onCancel={() => {
                props.onCancel();
            }}
            width={600}
            onOk={() => {
                handleOnSubmit();
            }}
            okButtonProps={{ loading: addFixRequestIsLoading, disabled: !selectedOption }}
            afterOpenChange={(open) => {
                if (open) {
                    seedFormFieldsOnOpen();
                }
            }}
            afterClose={() => {
                setSearchCriteria(null);
                setSelectedOption(null);
                setShouldChooseFromAssociatedWorkItems(true);
            }}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {props.requiresFixRequestsApprovals ?
                    <Text>
                        Choose the commit, PR, or ticket that you&apos;d like to pull in as a fix for this release. You
                        can optionally include details about why the fix is needed.
                    </Text>
                :   <Text>
                        Choose the commit, PR, or ticket to pull in as a fix for this release. You can optionally
                        include details about why the fix is needed.
                    </Text>
                }
                <Form
                    form={form}
                    style={{ marginTop: 24 }}
                    preserve={false}
                    initialValues={{
                        shouldChooseFromAssociatedWorkItems: true,
                    }}
                >
                    <Form.Item
                        {...formItemLayout}
                        name="shouldChooseFromAssociatedWorkItems"
                        hidden={!issueTrackingIntegrationsFromAutomation?.length}
                    >
                        <Radio.Group
                            style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                            defaultValue={shouldChooseFromAssociatedWorkItems}
                            onChange={() => {
                                setShouldChooseFromAssociatedWorkItems(!shouldChooseFromAssociatedWorkItems);
                                setSelectedOption(null);
                                setSearchCriteria(null);
                                setPendingAssociationTickets({});

                                form.setFieldsValue({ associatedItem: null });
                                form.setFieldsValue({ pendingAssociationTicket: null });
                            }}
                        >
                            <Radio value={true}>Browse work items on or against working branch</Radio>
                            <Radio value={false}>Search for a ticket</Radio>
                        </Radio.Group>
                    </Form.Item>
                    {shouldChooseFromAssociatedWorkItems ?
                        <Form.Item
                            {...formItemLayout}
                            name="associatedItem"
                            label="Associated work item"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please associate a PR, commit or ticket to the fix',
                                },
                            ]}
                            required
                        >
                            <WorkItemSelect
                                orgId={props.orgId}
                                appId={props.appId}
                                releaseId={props.releaseId}
                                stepRunId={props.stepRunId}
                            />
                        </Form.Item>
                    :   <Form.Item
                            {...formItemLayout}
                            name="pendingAssociationTicket"
                            label="Associated work item"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please associate a ticket to the fix',
                                },
                                {
                                    validator: (rule, value) => {
                                        if (selectedOption) {
                                            return Promise.resolve();
                                        }

                                        if (searchCriteria) {
                                            if (!value) {
                                                return Promise.reject();
                                            }
                                            if (!pendingAssociationTicketsResponse?.success) {
                                                return Promise.reject(pendingAssociationTicketsResponse.errorMessage);
                                            }

                                            if (!Object.keys(pendingAssociationTicketsResponse?.data || {}).length) {
                                                return Promise.reject(`No results found for ${searchCriteria}`);
                                            }
                                        }

                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            required
                        >
                            <AutoComplete
                                placeholder="Search by ticket ID"
                                onSelect={(value) =>
                                    pendingAssociationTickets &&
                                    value &&
                                    setSelectedOption(pendingAssociationTickets[value])
                                }
                                options={Object.values(pendingAssociationTickets).map((item) => ({
                                    value: item.ticket?.identifier,
                                }))}
                            >
                                <Input.Search
                                    enterButton={
                                        <Button
                                            disabled={
                                                getPendingAssociationTicketsIsLoading ||
                                                shouldSkipPendingAssociationTicketsSearch
                                            }
                                            loading={getPendingAssociationTicketsIsLoading}
                                            icon={
                                                <Icon
                                                    style={{ fontSize: '16px', color: antGrey.grey7 }}
                                                    component={SearchOutlined}
                                                />
                                            }
                                        />
                                    }
                                    onSearch={(value) => {
                                        setSelectedOption(null);
                                        setPendingAssociationTickets({});
                                        setSearchCriteria(value);
                                    }}
                                    // useGetPendingAssociationFixRequestsQuery gets triggered when search criteria changes
                                    // If we eventually want real time search, uncomment the following line
                                    // onChange={(value) => debouncedSearch(value)}
                                />
                            </AutoComplete>
                        </Form.Item>
                    }
                    {selectedOption && displayFeatureItemDetails()}
                    {selectedOption && selectedOption.fixRequest && (
                        <Alert
                            showIcon
                            closable={false}
                            style={{ marginTop: 10 }}
                            message={
                                <Text>
                                    This item is already associated with a fix request. Creating a new fix will replace
                                    the existing one.
                                </Text>
                            }
                            type="warning"
                        />
                    )}
                    {selectedOption?.code?.stackInfo && isAutoCherryPickAutomationEnabled && (
                        <Alert
                            showIcon
                            closable={false}
                            style={{ marginTop: 10 }}
                            message={
                                <Text>
                                    {codeIsCommit(selectedOption.code) ?
                                        `Commit ${selectedOption.code.identifier}`
                                    :   `PR #${selectedOption.code.identifier}`}{' '}
                                    is part of a stack of {selectedOption.code.stackInfo.size} pull requests. Each one
                                    builds on the one below it, so Runway recommends adding fix requests to all of them,
                                    starting from the bottom of the stack.
                                </Text>
                            }
                            type="warning"
                        />
                    )}
                    {props.fixRequestFormFieldsSettings.map((field, index) => (
                        <React.Fragment key={getFixRequestFormFieldId(field)}>
                            {field.description && (
                                <Form.Item
                                    {...formItemLayout}
                                    label={field.name}
                                    required={field.required}
                                    style={{ marginTop: index === 0 ? 24 : 0, marginBottom: 16 }}
                                >
                                    <div
                                        style={{
                                            marginTop: -8,
                                            padding: 12,
                                            border: `1px solid ${antGrey.grey5}`,
                                            borderRadius: 6,
                                        }}
                                    >
                                        <MarkdownComponent source={field.description} style={{ fontSize: 12 }} />
                                    </div>
                                </Form.Item>
                            )}
                            <Form.Item
                                {...formItemLayout}
                                name={`customField_${getFixRequestFormFieldId(field)}`}
                                label={field.description ? undefined : field.name}
                                initialValue={field.prePopulatedText || undefined}
                                rules={[
                                    { required: field.required, message: `${field.name} is required` },
                                    {
                                        validator: (_, value) => {
                                            if (
                                                field.minCharacters &&
                                                field.minCharacters > 0 &&
                                                value &&
                                                runeCount(value) < field.minCharacters
                                            ) {
                                                return Promise.reject(
                                                    new Error(
                                                        `${field.name} is too short (${runeCount(value)}/${field.minCharacters})`,
                                                    ),
                                                );
                                            }
                                            return Promise.resolve();
                                        },
                                    },
                                ]}
                                style={{ marginTop: index === 0 && !field.description ? 24 : 0 }}
                            >
                                <MarkdownEditor
                                    preview="edit"
                                    textareaProps={{ placeholder: field.placeholder || undefined }}
                                />
                            </Form.Item>
                        </React.Fragment>
                    ))}
                </Form>
                <AutomationSettingsBanner
                    automationId={props.autoCherryPickAutomationId}
                    appId={props.appId}
                    orgId={props.orgId}
                    isEnabled={isAutoCherryPickAutomationEnabled}
                    bannerStyle={{ marginTop: 15, padding: 12 }}
                    bannerText={
                        isAutoCherryPickAutomationEnabled ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>enabled</b>.
                                Fixes will be automatically cherry-picked into the release branch by Runway once all
                                checks have passed
                                {requireFixRequestsApprovals ?
                                    <> and the fix request has been approved.</>
                                :   <>.</>}
                            </Text>
                        :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                &quot;Pull (cherry-pick) fixes into the release&quot; (automation) is <b>disabled</b>.
                                Fixes will need to be manually cherry-picked and merged.
                            </Text>
                    }
                />
            </div>
        </Modal>
    );
};

export default AddAFixModal;
